/**
 * The dominant-slot (spotlight) selection for the unified meeting grid: exactly ONE rendered
 * tile is the large one, everything else packs the adaptive strip/grid beside it. The chain,
 * highest priority first:
 *
 * 1. PIN - the user clicked a tile; it stays dominant until they click it again. A pin never
 *    switches on its own.
 * 2. SCREEN SHARE - while any share is live, the NEWEST share is dominant (arrival order,
 *    tracked most-recent-first); several shares stay rendered as ordinary tiles.
 * 3. ACTIVE SPEAKER - the loudest speaking participant takes over after speaking CONTINUOUSLY
 *    for {@link SPEAKER_HYSTERESIS_MS} (1.5 s); a switch never happens mid-word for a fleeting
 *    sound, and the timer restarts when the speaker changes.
 * 4. FALLBACK - nobody pinned, nothing shared, nobody speaking: the previous dominant stays if
 *    still live, otherwise the first camera tile in derivation order (the local participant
 *    first, then stable identity order - the room's first publisher), so the slot never
 *    flaps.
 *
 * Tile keys: a participant's camera tile is keyed by their LiveKit identity, a screen-share
 * tile by the `screen:`-prefixed identity (one participant may render both at once, and each
 * is pinnable on its own).
 */

/** How long a candidate must speak continuously before taking the dominant slot. */
export const SPEAKER_HYSTERESIS_MS = 1500;

/** Prefix marking a share tile key (`screen:<identity>`); camera keys are bare identities. */
const SHARE_KEY_PREFIX = 'screen:';

/** The camera tile key of a participant (their bare LiveKit identity). */
export function cameraKey(identity: string): string {
  return identity;
}

/** The screen-share tile key of a participant's live share. */
export function shareKey(identity: string): string {
  return `${SHARE_KEY_PREFIX}${identity}`;
}

/** True when the key addresses a share tile rather than a camera tile. */
export function isShareKey(key: string): boolean {
  return key.startsWith(SHARE_KEY_PREFIX);
}

/** The participant identity a tile key points at (camera and share keys alike). */
export function identityOfKey(key: string): string {
  return isShareKey(key) ? key.slice(SHARE_KEY_PREFIX.length) : key;
}

/** The persisted half of the dominance resolver: current slot plus the hysteresis candidate. */
export interface DominanceState {
  /** The dominant tile key, or null while the room renders nothing. */
  readonly dominant: string | null;
  /** The speaker waiting out the hysteresis, or null. */
  readonly candidateKey: string | null;
  /** When the current candidate started speaking continuously (epoch ms), or null. */
  readonly candidateSince: number | null;
}

/** Everything the resolver needs for one step. All identity lists live-order their sources. */
export interface DominanceInput {
  /** Camera tile identities in derivation order (local first, remotes by identity). */
  readonly cameraIdentities: readonly string[];
  /** Live share identities, MOST RECENT FIRST (the tracked arrival order). */
  readonly shareIdentitiesNewestFirst: readonly string[];
  /** The user's pinned tile key, or null. */
  readonly pinnedKey: string | null;
  /** Active speaker identities, loudest first (RoomEvent.ActiveSpeakersChanged order). */
  readonly speakerIdentities: readonly string[];
  /** The current time in epoch ms (injected for testability). */
  readonly nowMs: number;
}

/** The initial state: nothing dominant yet (the first render falls straight to the fallback). */
export const INITIAL_DOMINANCE: DominanceState = {
  dominant: null,
  candidateKey: null,
  candidateSince: null,
};

/**
 * Advances the dominance state by one step. Pure: identical inputs produce an identical
 * result, and an unchanged result returns the PREVIOUS reference so callers can bail out of
 * re-renders (the state is resolved on every render and under StrictMode double-invocation).
 */
export function resolveDominant(prev: DominanceState, input: DominanceInput): DominanceState {
  const cameraKeys = input.cameraIdentities.map(cameraKey);
  const liveCameras = new Set(cameraKeys);
  const shareKeys = input.shareIdentitiesNewestFirst.map(shareKey);
  const live = new Set<string>([...liveCameras, ...shareKeys]);
  if (live.size === 0) {
    return nextOrSame(prev, INITIAL_DOMINANCE);
  }

  // 1. An explicit pin wins while the pinned tile stays live. A pin on a share that just
  // stopped carries onto the same participant's camera tile: the user pinned the person, not
  // the stream lifetime (and the reverse - a camera pin survives nothing, the share takes the
  // slot while it is live).
  const { pinnedKey } = input;
  if (pinnedKey !== null) {
    const pinnedTileKey = live.has(pinnedKey)
      ? pinnedKey
      : pickLiveCamera(liveCameras, identityOfKey(pinnedKey));
    if (pinnedTileKey !== null) {
      return nextOrSame(prev, {
        dominant: pinnedTileKey,
        candidateKey: null,
        candidateSince: null,
      });
    }
  }

  // 2. Any live share makes the newest share dominant, immediately.
  if (shareKeys.length > 0) {
    const newest = shareKeys[0] ?? null;
    return nextOrSame(prev, { dominant: newest, candidateKey: null, candidateSince: null });
  }

  // The stable fallback: the previous dominant if it survived, else the first camera tile.
  const stableDominant =
    prev.dominant !== null && live.has(prev.dominant) ? prev.dominant : (cameraKeys[0] ?? null);

  // 3. The loudest speaking participant with a live camera is the takeover candidate.
  const candidate = speakingCameraKey(input.speakerIdentities, liveCameras);
  if (candidate === null || candidate === stableDominant) {
    return nextOrSame(prev, {
      dominant: stableDominant,
      candidateKey: null,
      candidateSince: null,
    });
  }

  // 4. A different speaker must hold the floor continuously before taking over; the timer
  // restarts whenever the candidate changes.
  const since =
    prev.candidateKey === candidate && prev.candidateSince !== null
      ? prev.candidateSince
      : input.nowMs;
  if (input.nowMs - since >= SPEAKER_HYSTERESIS_MS) {
    return nextOrSame(prev, { dominant: candidate, candidateKey: null, candidateSince: null });
  }
  return nextOrSame(prev, {
    dominant: stableDominant,
    candidateKey: candidate,
    candidateSince: since,
  });
}

/** The live camera key of `identity`, or null while that camera is not rendered. */
function pickLiveCamera(liveCameras: ReadonlySet<string>, identity: string): string | null {
  const key = cameraKey(identity);
  return liveCameras.has(key) ? key : null;
}

/** The first active speaker that owns a live camera tile, as a tile key. */
function speakingCameraKey(
  speakerIdentities: readonly string[],
  liveCameras: ReadonlySet<string>,
): string | null {
  for (const identity of speakerIdentities) {
    const key = cameraKey(identity);
    if (liveCameras.has(key)) {
      return key;
    }
  }
  return null;
}

/** Returns the proposed state, or the previous reference when nothing actually changed. */
function nextOrSame(prev: DominanceState, next: DominanceState): DominanceState {
  return prev.dominant === next.dominant &&
    prev.candidateKey === next.candidateKey &&
    prev.candidateSince === next.candidateSince
    ? prev
    : next;
}
